import { useState } from "react";
import type { CellType, ColumnDef } from "../types";
import { uid } from "../utils";

interface Props {
  onAdd: (col: ColumnDef) => void;
  onCancel: () => void;
}

const TYPE_LABELS: Record<CellType, string> = {
  text: "Text (short)",
  longText: "Text (long)",
  status: "Status (standard)",
  badge: "Custom dropdown",
  number: "Number",
  date: "Date",
};

const TYPE_ORDER: CellType[] = [
  "text",
  "longText",
  "status",
  "badge",
  "number",
  "date",
];

// Inline form for adding a new column. Shown when user clicks "+ Add column"
// in column-edit mode. Lives inline (not a modal) so the rest of the table
// stays visible for context.
export function AddColumnForm({ onAdd, onCancel }: Props) {
  const [label, setLabel] = useState("");
  const [type, setType] = useState<CellType>("text");
  const [optionsText, setOptionsText] = useState("");

  function submit() {
    const trimmed = label.trim();
    if (!trimmed) return;
    const col: ColumnDef = {
      id: uid(),
      label: trimmed,
      type,
    };
    if (type === "badge") {
      const opts = optionsText
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean);
      if (opts.length > 0) col.options = opts;
    }
    onAdd(col);
  }

  return (
    <div className="bg-white rounded-xl ring-1 ring-slate-200 shadow-lg p-4 w-80 space-y-3">
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
        Add column
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Label</label>
        <input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
            if (e.key === "Escape") onCancel();
          }}
          placeholder="e.g. Target close date"
          className="w-full text-sm px-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-500 mb-1">Type</label>
        <select
          value={type}
          onChange={(e) => setType(e.target.value as CellType)}
          className="w-full text-sm px-3 py-1.5 rounded-lg border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          {TYPE_ORDER.map((t) => (
            <option key={t} value={t}>
              {TYPE_LABELS[t]}
            </option>
          ))}
        </select>
      </div>
      {type === "badge" && (
        <div>
          <label className="block text-xs text-slate-500 mb-1">
            Dropdown options (one per line)
          </label>
          <textarea
            value={optionsText}
            onChange={(e) => setOptionsText(e.target.value)}
            placeholder={"Option A\nOption B\nOption C"}
            rows={4}
            className="w-full text-sm px-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-400 font-mono"
          />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button
          onClick={onCancel}
          className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={!label.trim()}
          className="px-3 py-1.5 text-xs text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 rounded-lg transition-colors"
        >
          Add column
        </button>
      </div>
    </div>
  );
}
